import { useApiClient } from "~/composables/useApiClient";
export interface ResetPassword {
  user_origin?: string;
  code: string;
  password: string;
  passwordConfirmation: string;
}
export interface ResetPasswordParams extends Omit<
  ResetPassword,
  "user_origin"
> {}

export interface UserToCreate extends Omit<User, "id" | "userOrigin"> {}

interface FormLogin {
  identificator: string;
  password: string;
}
interface UserApi {
  id?: number;
  names: string;
  paternal_surname: string;
  maternal_surname: string;
  email: string;
  user_origin: string;
  gender?: string;
  custom_gender?: string;
  birthdate?: string;
  document_type?: string;
  custom_document_type?: string;
  document_number?: string;
  education_level?: string;
  password?: string;
  has_started_survey?: boolean;
}
type PersonalInfoToUpdate = Pick<
  User,
  "gender" | "customGender" | "birthdate" | "educationLevel"
>;

class PersonalInfoMapperToApi implements DataMapper<
  Partial<PersonalInfoToUpdate>,
  Partial<UserApi>
> {
  map(domainModel: Partial<PersonalInfoToUpdate>): Partial<UserApi> {
    return {
      gender: domainModel.gender,
      custom_gender: domainModel.customGender,
      birthdate: domainModel.birthdate
        ? domainModel.birthdate.toISOString()
        : undefined,
      education_level: domainModel.educationLevel
    };
  }
}

class UserMapperToDomain implements DataMapper<UserApi, User> {
  map(apiModel: UserApi): User {
    return {
      id: apiModel.id as number,
      names: apiModel.names,
      paternalSurname: apiModel.paternal_surname,
      maternalSurname: apiModel.maternal_surname,
      email: apiModel.email,
      userOrigin: apiModel.user_origin,
      documentType: apiModel.document_type ?? "",
      customDocumentType: apiModel.custom_document_type ?? "",
      documentNumber: apiModel.document_number ?? "",
      gender: apiModel.gender ?? "",
      customGender: apiModel.custom_gender ?? "",
      birthdate: apiModel.birthdate ? new Date(apiModel.birthdate) : new Date(),
      educationLevel: apiModel.education_level ?? "",
      has_started_survey: apiModel.has_started_survey
    };
  }
}
class UserMapperToApi implements DataMapper<Partial<User>, UserApi> {
  map(domainModel: Partial<User>): UserApi {
    return {
      id: domainModel.id,
      names: domainModel.names!,
      paternal_surname: domainModel.paternalSurname!,
      maternal_surname: domainModel.maternalSurname!,
      email: domainModel.email!,
      password: domainModel.password,
      document_type: domainModel.documentType,
      custom_document_type: domainModel.customDocumentType,
      document_number: domainModel.documentNumber,
      gender: domainModel.gender,
      custom_gender: domainModel.customGender,
      birthdate: domainModel.birthdate
        ? domainModel.birthdate.toISOString()
        : undefined,
      education_level: domainModel.educationLevel,
      user_origin: domainModel.userOrigin!
    };
  }
}
export interface CheckDocumentNumberResponse {
  available: boolean;
}

export interface CheckEmailResponse {
  available: boolean;
}

export interface AuthRepository {
  create: (user: UserToCreate) => Promise<ResponseApi<User>>;
  updatePersonalInfo: (
    user: PersonalInfoToUpdate
  ) => Promise<ResponseApi<User>>;
  login: (form: FormLogin) => Promise<ResponseApi<unknown>>;
  logout: () => Promise<ResponseApi<unknown>>;

  validateEmail: (codeConfirmation: string) => Promise<ResponseApi<boolean>>;
  resendConfirmationEmail: (
    codeConfirmation: string
  ) => Promise<ResponseApi<boolean>>;

  forgotPassword: (email: string) => Promise<ResponseApi<boolean>>;
  resetPassword: (params: ResetPasswordParams) => Promise<ResponseApi<boolean>>;
  me: () => Promise<ResponseApi<User>>;
  updatePassword: (
    oldPassword: string,
    newPassword: string
  ) => Promise<ResponseApi<boolean>>;
  checkDocumentNumber: (
    documentNumber: string,
    documentType?: string,
    customDocumentType?: string
  ) => Promise<ResponseApi<CheckDocumentNumberResponse>>;
  checkEmail: (email: string) => Promise<ResponseApi<CheckEmailResponse>>;
}

export const useAuthRepository: () => AuthRepository = () => {
  const { config } = useConfig();
  const user_origin = config.USER_ORIGIN;
  const create = (user: UserToCreate): Promise<ResponseApi<User>> => {
    return useApiClient("/auth/register", {
      method: "POST",
      body: new UserMapperToApi().map({ ...user, userOrigin: user_origin }),
      mapper: new UserMapperToDomain()
    }) as Promise<ResponseApi<User>>;
  };

  const updatePersonalInfo = (
    user: PersonalInfoToUpdate
  ): Promise<ResponseApi<User>> => {
    /* Obtiene el id del usuario autenticado en el backend */
    return useApiClient("/users", {
      method: "PUT",
      body: new PersonalInfoMapperToApi().map(user),
      mapper: new UserMapperToDomain()
    }) as Promise<ResponseApi<User>>;
  };

  const login = async (form: FormLogin): Promise<ResponseApi<unknown>> => {
    return await useApiClient("/auth/login", {
      method: "POST",
      body: { ...form, user_origin },
      credentials: "include"
    });
  };
  const logout = async (): Promise<ResponseApi<unknown>> => {
    return await useApiClient("/auth/logout", {
      method: "POST",
      credentials: "include"
    });
  };
  const validateEmail = async (
    codeConfirmation: string
  ): Promise<ResponseApi<boolean>> => {
    return await useApiClient("/auth/email-confirmation", {
      method: "POST",
      params: { code: codeConfirmation },
      credentials: "include"
    });
  };

  const resendConfirmationEmail = (
    codeConfirmation: string
  ): Promise<ResponseApi<boolean>> => {
    return useApiClient("/auth/resend-confirmation", {
      method: "POST",
      body: { code: codeConfirmation }
    });
  };

  const forgotPassword = (email: string): Promise<ResponseApi<boolean>> => {
    return useApiClient("/auth/forgot-password", {
      method: "POST",
      body: { email, user_origin }
    });
  };

  // Tiene que crear una cookie de sesión para inciar sesión automáticamente luego de resetear la contraseña
  const resetPassword = (
    params: ResetPasswordParams
  ): Promise<ResponseApi<boolean>> => {
    return useApiClient("auth/reset-password", {
      method: "POST",
      body: { ...params, user_origin }
    });
  };

  const me = (): Promise<ResponseApi<User>> => {
    return useApiClient("/auth/me", {
      method: "GET",
      mapper: new UserMapperToDomain(),
      key: `auth/me-${Date.now()}`
    }) as Promise<ResponseApi<User>>;
  };

  const updatePassword = (
    oldPassword: string,
    newPassword: string
  ): Promise<ResponseApi<boolean>> => {
    return useApiClient("/auth/update-password", {
      method: "POST",
      body: { oldPassword, newPassword }
    });
  };

  const checkDocumentNumber = (
    documentNumber: string,
    documentType?: string,
    customDocumentType?: string
  ): Promise<ResponseApi<CheckDocumentNumberResponse>> => {
    return useApiClient("/auth/check-document-number", {
      method: "POST",
      body: {
        document_number: documentNumber,
        document_type: documentType,
        custom_document_type: customDocumentType
      }
    }) as Promise<ResponseApi<CheckDocumentNumberResponse>>;
  };

  const checkEmail = (
    email: string
  ): Promise<ResponseApi<CheckEmailResponse>> => {
    return useApiClient("/auth/check-email", {
      method: "POST",
      body: { email },
      key: `auth/check-email-${Date.now()}`
    }) as Promise<ResponseApi<CheckEmailResponse>>;
  };

  return {
    create,
    login,
    validateEmail,
    resendConfirmationEmail,
    forgotPassword,
    resetPassword,
    me,
    logout,
    updatePersonalInfo,
    updatePassword,
    checkDocumentNumber,
    checkEmail
  };
};
